import { Box, ButtonBase, Collapse } from '@mui/material';
import { forwardRef, PropsWithChildren, useState } from 'react';

import colors from '../../constants/colors';

interface ExpandableListProps {
  content: (openState: boolean, text?: string) => React.ReactElement;
  id: string;
  open?: boolean;
  onClick?: (openState: boolean) => void; //onClick should be for handling side effects when the component is uncontrolled
  handleToggle?: () => void; //handleToggle should be for handling side effects when the component is controlled
  disabled?: boolean;
}

const ExpandableList = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ExpandableListProps>
>(({ children, content, id, handleToggle, open, onClick, disabled }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const toggleOpen = () => {
    if (handleToggle) {
      handleToggle();
    } else {
      setIsOpen(!isOpen);
      if (onClick) {
        onClick(isOpen);
      }
    }
  };
  //removing any spaces as id attribute does not allow spaces, and converting to kebab case.
  id = id.replaceAll(' ', '-').toLowerCase();

  return (
    <>
      <Box
        sx={{
          outline: isFocused ? `solid 2px ${colors.csaaBlack}` : 'none',
          borderRadius: 1,
        }}
      >
        <ButtonBase
          aria-expanded={disabled ? undefined : (open ?? isOpen)}
          aria-controls={disabled ? undefined : `${id}-content`}
          data-testid={`${id}-expandable-list-button`}
          disabled={disabled}
          disableRipple
          id={`${id}-button`}
          onClick={toggleOpen}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            cursor: 'pointer',
            width: 1,
            height: 'fit-content',
          }}
          ref={ref}
        >
          {content(open ?? isOpen)}
        </ButtonBase>
        {!disabled && (
          <Collapse
            aria-labelledby={`${id}-button`}
            id={`${id}-content`}
            in={open ?? isOpen}
            role="region"
          >
            {children}
          </Collapse>
        )}
      </Box>
    </>
  );
});

ExpandableList.displayName = 'ExpandableList';

export default ExpandableList;
